import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { ChevronDown, CirclePlus } from 'lucide-react';

import { useFieldWiring } from '@nesso/ui/hooks/use-field-wiring';
import { cn } from '@nesso/ui/lib/utils';
import {
    Command,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@nesso/ui/primitives/command';
import { Field, FieldSlot } from '@nesso/ui/primitives/field';
import { inputInvalidClassName } from '@nesso/ui/primitives/input';
import { Popover, PopoverContent, PopoverTrigger } from '@nesso/ui/primitives/popover';
import { selectTriggerClassName } from '@nesso/ui/primitives/select';

import { getFilteredServiceOptions, serviceCatalogOptions } from '@/entities/service-catalog';
import { useSheetStore } from '@/shared/stores/sheet-store';

import { getScheduleSessionServiceOptions } from '../../../lib/get-schedule-session-service-options';

type Props = {
    value: string;
    error?: boolean | string;
    onSelect: (code: string) => void;
    // Lets the call site fuse this trigger with an adjacent control, like the fee input sharing
    // one divider in `schedule-session-form.tsx`.
    triggerClassName?: string;
    // A flex item's `min-width` defaults to its content's own (unwrappable) size — wider than the
    // fused layout's 280px share — so shrinking it that far needs an explicit override.
    fieldClassName?: string;
};

// Popover + Command, not Combobox — the design puts a search panel under a select-style trigger
// with an "Add new service" action `Combobox` has no slot for.
export function ScheduleSessionServicePicker({
    value,
    error,
    onSelect,
    triggerClassName,
    fieldClassName,
}: Props) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { data: catalog } = useQuery(serviceCatalogOptions());
    const wiring = useFieldWiring({ error });
    const openSheet = useSheetStore((state) => state.openSheet);

    const options = getScheduleSessionServiceOptions(catalog ?? { services: [] });
    const filteredOptions = getFilteredServiceOptions(options, search);
    const selected = options.find((option) => option.code === value);

    function selectOption(code: string) {
        onSelect(code);
        setSearch('');
        setOpen(false);
    }

    function addNewService() {
        openSheet({
            type: 'services/save',
            label: 'Add service',
            data: { onCreated: selectOption },
        });
        setSearch('');
        setOpen(false);
    }

    return (
        <Field data-invalid={wiring.dataInvalid} className={fieldClassName}>
            {/* modal: gives the portaled list its own scroll lock; the dialog's lock blocks wheel and touch. */}
            <Popover open={open} onOpenChange={setOpen} modal={true}>
                <PopoverTrigger
                    {...wiring.controlProps}
                    type="button"
                    className={cn(
                        selectTriggerClassName,
                        inputInvalidClassName,
                        'bg-input-background justify-between gap-2',
                        triggerClassName,
                    )}
                >
                    <span className="truncate">{selected ? selected.label : 'Select service'}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </PopoverTrigger>
                <PopoverContent
                    // Spans the fused service+fee control (345px), not just this trigger's own
                    // 280px — not on the 4px spacing scale, hence the arbitrary value.
                    className="w-[345px] max-w-(--radix-popover-content-available-width) p-0"
                    align="start"
                    collisionPadding={24}
                >
                    {/* `shouldFilter={false}` because filtering happens in
                    `getFilteredServiceOptions`. cmdk's `CommandEmpty` never shows
                    since "Add new service" always counts as a registered item — hence the plain div
                    below. */}
                    <Command shouldFilter={false}>
                        <CommandInput
                            value={search}
                            onValueChange={setSearch}
                            placeholder="Search"
                        />
                        <CommandList>
                            {filteredOptions.length > 0 ? (
                                <CommandGroup>
                                    {filteredOptions.map((option) => (
                                        <CommandItem
                                            key={option.code}
                                            value={option.code}
                                            onSelect={() => selectOption(option.code)}
                                            // Overrides the primitive's default 44px row height.
                                            className="py-1.5"
                                        >
                                            <span className="truncate">{option.label}</span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            ) : (
                                <div className="py-6 text-center text-sm">No service found.</div>
                            )}
                            <CommandSeparator />
                            <CommandGroup>
                                <CommandItem onSelect={addNewService} className="py-1.5">
                                    <CirclePlus className="h-4 w-4" />
                                    Add new service
                                </CommandItem>
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            <FieldSlot errorMessage={wiring.errorMessage} errorId={wiring.errorId} />
        </Field>
    );
}
